import { PageTitle } from "@/components/page-title";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  PropsWithUser,
  ReconciliationStats,
  FinancialAccountAmounts,
} from "@/types";
import { formatMoney } from "@/utils/format-money";
import { Link } from "@inertiajs/react";
import { ListTodoIcon } from "lucide-react";

type Props = {
  reconciliation_stats: ReconciliationStats;
  financial_accounts_amounts: FinancialAccountAmounts[];
};

function DashboardShow({
  reconciliation_stats,
  financial_accounts_amounts,
}: PropsWithUser<Props>) {
  const hasUnreconciledItems =
    reconciliation_stats.unreconciled_invoices_count > 0 ||
    reconciliation_stats.unreconciled_payment_transactions_count > 0;

  return (
    <>
      <PageTitle>Dashboard</PageTitle>
      <div className="mt-4 grid grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <CardTitle>Unreconciled items</CardTitle>
              <ListTodoIcon className="text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            {hasUnreconciledItems ? (
              <>
                <div className="flex items-center justify-between gap-4">
                  <div className="text-muted-foreground">Invoices</div>
                  <div className="font-medium">
                    {reconciliation_stats.unreconciled_invoices_count}
                  </div>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div className="text-muted-foreground">Transactions</div>
                  <div className="font-medium">
                    {
                      reconciliation_stats.unreconciled_payment_transactions_count
                    }
                  </div>
                </div>
              </>
            ) : (
              <div className="text-muted-foreground">
                No unreconciled items found.
              </div>
            )}
          </CardContent>
          {hasUnreconciledItems && (
            <CardFooter>
              <Button asChild className="w-full">
                <Link href="/reconciliations/new">Reconcile</Link>
              </Button>
            </CardFooter>
          )}
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <CardTitle>Allocation</CardTitle>
              <ListTodoIcon className="text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-end gap-4 font-mono">
              <div className="text-muted-foreground">base</div>
              <div className="text-muted-foreground">vat</div>
              <div>total</div>
            </div>

            {financial_accounts_amounts.map((amounts, index) => (
              <div key={index}>
                <div>{amounts.name}</div>
                <div className="flex items-center justify-end gap-4 font-mono">
                  <div className="text-muted-foreground">
                    {formatMoney(amounts.base_amount)}
                  </div>
                  <div className="text-muted-foreground">
                    {formatMoney(amounts.vat_amount)}
                  </div>
                  <div>{formatMoney(amounts.total_amount)}</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

export default DashboardShow;
